import { useMemo, useSyncExternalStore } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useView } from "@/context/ViewContext";
import {
  isMobileNavHidden,
  subscribeMobileNavHidden,
} from "./mobileNavVisibility";
import {
  Inbox,
  Home,
  Key,
  Wrench,
  Settings,
  CreditCard,
  Gauge,
  Receipt,
  Calendar,
} from "lucide-react";

export default function MobileBottomNav({ unreadInboxCount = 0 }) {
  const { t } = useTranslation(["common", "maintenance"]);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentView } = useView();
  // Full-screen surfaces (e.g. a route-backed editor section on a phone) ask
  // for the bar to step aside so it never overlaps their own sticky footer.
  const hidden = useSyncExternalStore(
    subscribeMobileNavHidden,
    isMobileNavHidden,
    () => false,
  );

  const navItems = useMemo(() => {
    if (currentView === "resident") {
      return [
        {
          id: "resident-home",
          label: t("nav.home", "Home"),
          icon: Home,
          path: "/",
          isActive: (pathname) => pathname === "/",
          badge: unreadInboxCount,
        },
        {
          id: "resident-leases",
          label: t("nav.tenantLease", "My Lease"),
          icon: Key,
          path: "/tenant/lease",
          isActive: (pathname) => pathname.startsWith("/tenant/lease"),
        },
        {
          id: "resident-repairs",
          label: t("nav.group.maintenance", "Repairs"),
          icon: Wrench,
          path: "/maintenance",
          isActive: (pathname) => pathname.startsWith("/maintenance"),
        },
        {
          id: "resident-payments",
          label: t("nav.tenantPayments", "Payments"),
          icon: CreditCard,
          path: "/tenant/payments",
          isActive: (pathname) => pathname.startsWith("/tenant/payments"),
        },
        {
          id: "resident-settings",
          label: t("nav.settings", "Settings"),
          icon: Settings,
          path: "/settings",
          isActive: (pathname) => pathname.startsWith("/settings"),
        },
      ];
    }

    if (currentView === "contractor") {
      return [
        {
          id: "contractor-home",
          label: t("nav.inbox", "Inbox"),
          icon: Inbox,
          path: "/",
          isActive: (pathname) => pathname === "/",
          badge: unreadInboxCount,
        },
        {
          id: "contractor-workorders",
          label: t("nav.maintenanceRequests", "Work Orders"),
          icon: Wrench,
          path: "/maintenance",
          isActive: (pathname) =>
            pathname === "/maintenance" ||
            (pathname.startsWith("/maintenance/") &&
              !pathname.startsWith("/maintenance/statements")),
        },
        {
          id: "contractor-calendar",
          label: t("nav.calendar", "Calendar"),
          icon: Calendar,
          path: "/calendar",
          isActive: (pathname) =>
            pathname.startsWith("/calendar") || pathname.startsWith("/vendor/calendar"),
        },
        {
          id: "contractor-statements",
          label: t("nav.vendorStatements", "Billing"),
          icon: Receipt,
          path: "/maintenance/statements",
          isActive: (pathname) =>
            pathname.startsWith("/maintenance/statements") ||
            pathname.startsWith("/vendor/statements"),
        },
        {
          id: "contractor-settings",
          label: t("nav.settings", "Settings"),
          icon: Settings,
          path: "/settings",
          isActive: (pathname) => pathname.startsWith("/settings"),
        },
      ];
    }

    // Default: Operations view
    return [
      {
        id: "operations-inbox",
        label: t("nav.inbox", "Inbox"),
        icon: Inbox,
        path: "/",
        isActive: (pathname) => pathname === "/",
        badge: unreadInboxCount,
      },
      {
        id: "operations-properties",
        label: t("nav.properties", "Properties"),
        icon: Home,
        path: "/properties",
        isActive: (pathname) => pathname.startsWith("/properties"),
      },
      {
        id: "operations-rentals",
        label: t("nav.rentals", "Rentals"),
        icon: Key,
        path: "/rentals",
        isActive: (pathname) => pathname.startsWith("/rentals"),
      },
      {
        id: "operations-maintenance",
        label: t("nav.group.maintenance", "Maintenance"),
        icon: Wrench,
        path: "/maintenance",
        isActive: (pathname) => pathname.startsWith("/maintenance"),
      },
      {
        id: "operations-settings",
        label: t("nav.settings", "Settings"),
        icon: Settings,
        path: "/settings",
        isActive: (pathname) => pathname.startsWith("/settings"),
      },
    ];
  }, [currentView, t, unreadInboxCount]);

  if (hidden) return null;

  return (
    <nav
      aria-label={t("nav.bottomNavigation", "Mobile Navigation")}
      className="fixed bottom-0 left-0 right-0 border-t border-border bg-card/95 backdrop-blur-md z-40 flex items-center justify-around h-[calc(4rem+env(safe-area-inset-bottom,0px))] md:hidden px-2 pb-[env(safe-area-inset-bottom,0px)] select-none"
    >
      {navItems.map((item) => {
        const Icon = item.icon;
        const active = item.isActive(location.pathname);
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => navigate(item.path)}
            aria-label={item.label}
            aria-current={active ? "page" : undefined}
            className="flex flex-col items-center justify-center flex-1 h-full min-h-[44px] min-w-[44px] py-1 cursor-pointer transition-all duration-100 outline-none relative focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            <div className="relative">
              <Icon
                size={20}
                className={cn(
                  "transition-transform duration-100",
                  active ? "text-primary scale-110" : "text-muted-foreground"
                )}
              />
              {item.badge !== undefined && item.badge > 0 && (
                <span className="absolute -top-1 -right-2 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-overline-size font-bold text-destructive-foreground ring-2 ring-card">
                  {item.badge > 99 ? "99+" : item.badge}
                </span>
              )}
            </div>
            <span
              className={cn(
                "text-overline-size mt-1 tracking-tight truncate max-w-full px-0.5 leading-tight text-center",
                active ? "text-primary font-semibold" : "text-muted-foreground"
              )}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
